import { HYC_PRODUCT_ID, IGC_PRODUCT_ID } from '@/creditflowresearch/domain/Product/constants';
import { EMERGING_MARKET_ID, US_MARKET_ID } from '@/creditflowresearch/domain/Market/constants';
import { ALL_SEGMENT_IDS, SSA_SEGMENT_ID } from '@/creditflowresearch/domain/Segment/constants';
import { MarketSegmentGroupType } from '@creditflowresearch/user-plan/vo/IMarketSegmentSelection';

interface ISegmentSelectionContext {
  productIds?: string[],
  marketIds?: string[],
}

/** One row that owns a segment list — a corporate product, or the EM market. */
export interface ISegmentScope {
  id: string,
  type: MarketSegmentGroupType,
}

const CORPORATE_PRODUCT_IDS: string[] = [HYC_PRODUCT_ID, IGC_PRODUCT_ID];

const SSA_EXCLUDED_MARKET_IDS: string[] = [US_MARKET_ID];

/** High yield has no SSA issuance in the US alone. */
const isSsaHiddenForProduct = (productId: string, marketIds: string[] = []): boolean =>
  productId === HYC_PRODUCT_ID
  && marketIds.length === 1
  && marketIds[0] === US_MARKET_ID;

export const isSsaDefaultedForProducts = (
  productIds: string[],
  marketIds: string[] = [],
): boolean => productIds.includes(IGC_PRODUCT_ID)
  && !(marketIds.length > 0 && marketIds.every((id) => SSA_EXCLUDED_MARKET_IDS.includes(id)));

export const getSegmentScopes = (
  { productIds = [], marketIds = [] }: ISegmentSelectionContext,
): ISegmentScope[] => {
  if (marketIds.includes(EMERGING_MARKET_ID)) {
    return [{ id: EMERGING_MARKET_ID, type: MarketSegmentGroupType.Market }];
  }

  return productIds
    .filter((id) => CORPORATE_PRODUCT_IDS.includes(id))
    .map((id) => ({ id, type: MarketSegmentGroupType.Product }));
};

export const getVisibleSegmentIds = (scope: ISegmentScope, marketIds: string[] = []): string[] => (
  scope.type === MarketSegmentGroupType.Product && isSsaHiddenForProduct(scope.id, marketIds)
    ? ALL_SEGMENT_IDS.filter((id) => id !== SSA_SEGMENT_ID)
    : [...ALL_SEGMENT_IDS]
);

/** Only the starting ticks — every visible segment stays pickable. */
export const getDefaultSegmentIds = (scope: ISegmentScope, marketIds: string[] = []): string[] => {
  const visible = getVisibleSegmentIds(scope, marketIds);
  const startsWithSsa = scope.type === MarketSegmentGroupType.Market
    || isSsaDefaultedForProducts([scope.id], marketIds);

  return startsWithSsa ? visible : visible.filter((id) => id !== SSA_SEGMENT_ID);
};

/** One SSA filter button serves the whole page selection, so it only goes when no product offers it. */
export const isSsaSegmentHidden = (
  { productIds = [], marketIds = [] }: ISegmentSelectionContext,
): boolean => productIds.length > 0
  && productIds.every((id) => isSsaHiddenForProduct(id, marketIds));
